import { ReactElement } from 'react';

import { Node } from '@tiptap/pm/model';
import { EditorState, Transaction } from '@tiptap/pm/state';
import { NodeViewProps } from '@tiptap/react';

import { IconProps } from '@components/icons';
import { BaseIcon } from '@components/icons/base';
import { IconMappingOff } from '@components/icons/mapping-off';
import { IconMappingOn } from '@components/icons/mapping-on';
import { SelectOption } from '@components/select';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { MakePropertiesRequired, XOR } from '@utils/types';

import { BaseFieldMappingTextEditorProps } from '../field-mapping-text-editor';
import {
  createPlaceholderHtml,
  createPlaceholderNode,
  FieldMappingContext,
  isSourceActionMissing,
  PlaceholderContainer,
} from '../helpers';
import { EditorValue } from '../text-editor';

export type MappableActionVariable = {
  source: 'action';
  action: string;
  field: string;
  label: string;
  type: FieldType;
  template?: {
    name: string;
    type: FieldType;
    label: string;
  }[];
};

export type MappableObjectVariable = {
  source: 'traveller' | 'traveller_journey' | 'journey' | 'company' | 'company_traveller';
  field: string;
  label: string;
  type: FieldType;
};

export type MappableVariable = MappableActionVariable | MappableObjectVariable;

export type VariableValue = {
  value: string;
  variables?: Record<string, MappableVariable>;
  type?: 'select';
};

export type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'date'
  | 'file'
  | 'file_multi'
  | 'options'
  | 'select'
  | 'multi_select'
  | 'number'
  | 'paragraph'
  | 'separator'
  | 'array'
  | 'array_of_strings'
  | 'boolean'
  | 'form'
  | 'select'
  | 'decimal'
  | 'webhookUrl';

export type FieldMappingFieldItem = {
  source: string;
  field: string;
  label: string;
  type?: FieldType;
  parentType?: string;
  action?: string;
  description?: string;
  options?: SelectOption[] | { value: string; display: string }[];
  icon?: ReactElement<IconProps>;
  iconRight?: ReactElement<IconProps>;
  variable?: string;
  isActive?: boolean;
};

export type FieldMappingGroup = {
  id: string;
  icon?: JSX.Element;
  label: string;
  description?: string;
  fields: FieldMappingFieldItem[];
  action?: string;
};

export type FieldMappingData = XOR<
  {},
  {
    variableGroups: FieldMappingGroup[];
    fieldMappingId: string;
  }
>;

export type FieldMappingVariables = {
  [key in string]: FieldMappingFieldItem;
};

export type FieldMappingEditorValue = EditorValue<{ variables?: FieldMappingVariables }>;

export type ExtendedFieldMappingEditorValue = FieldMappingEditorValue | { value: string };

export type BaseFieldMappingFieldProps<T = ExtendedFieldMappingEditorValue> =
  MakePropertiesRequired<Omit<BaseFieldMappingTextEditorProps, 'onChange'>, 'variableGroups'> & {
    onChange?(value: T): void;
    defaultValue?: T;
  };

export const getVariableName = (inputObject: VariableValue) => {
  if (inputObject.variables) {
    const variable = Object.values(inputObject.variables)[0];
    return extractNameFromVariable(variable);
  }
  return '';
};

export const extractNameFromVariable = (variable: MappableVariable) => {
  if (variable.source === 'action') {
    return `${variable.action}__${variable.field}`;
  } else {
    return `${variable.source}__${variable.field}`;
  }
};

export const isFieldMappingEditorValue = (
  value: ExtendedFieldMappingEditorValue
): value is FieldMappingEditorValue => Boolean((value as FieldMappingEditorValue).variables);

const tag = 'field-mapped-property';

const attributes = ['source', 'label', 'field', 'action', 'type', 'tooltip', 'variable'];

export const getPlaceholderHtml = createPlaceholderHtml({ tag, attributes });

const NodeComponent = (props: NodeViewProps) => {
  const isSourceMissing = isSourceActionMissing(props.node.attrs);
  const variant = isSourceMissing ? 'error' : 'fieldMapped';
  const icon = isSourceMissing ? IconMappingOff : IconMappingOn;

  return <PlaceholderContainer variant={variant} icon={icon} {...props} />;
};

export const FieldMappingNode = createPlaceholderNode({
  attributes,
  renderText: getPlaceholderHtml,
  tag,
  nodeComponent: NodeComponent,
});

export const transformOutput = (text: string) => {
  const parser = new DOMParser();
  const html = parser.parseFromString(text, 'text/html');
  const variableElements = [...html.getElementsByTagName(tag)];
  const variables = {} as FieldMappingVariables;
  variableElements.forEach((element) => {
    const field = element.attributes.getNamedItem('field')?.nodeValue;
    const source = element.attributes.getNamedItem('source')?.nodeValue;
    const label = element.attributes.getNamedItem('label')?.nodeValue;
    const action = element.attributes.getNamedItem('action')?.nodeValue;
    const type = element.attributes.getNamedItem('type')?.nodeValue;
    const variable = element.attributes.getNamedItem('variable')?.nodeValue;
    const variableName =
      variable && variable !== 'undefined'
        ? variable
        : source?.startsWith('journey_attribute__') ||
          source?.startsWith('company_traveller_attribute__')
        ? `${source}__${field}`
        : `${source}_${field}`;
    element.replaceWith(`{{ ${variableName} }}`);
    variables[variableName] = {
      source,
      field,
      label,
      action,
      type,
    } as FieldMappingFieldItem;
  });

  return { value: html.body.innerHTML, variables };
};

export const isTravellerVariableSource = (variableSource: FieldMappingFieldItem['source']) => {
  return (
    variableSource === 'company_traveller' ||
    variableSource === 'traveller' ||
    variableSource === 'traveller_journey' ||
    variableSource.startsWith('company_traveller_attribute') ||
    variableSource.startsWith('journey_attribute')
  );
};

const getVariableSource = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[]
) => {
  const { t } = getScopedTranslate(locale);
  if (isTravellerVariableSource(variable.source)) {
    return t('traveller');
  }
  if (variable.source === 'company') {
    return t('company');
  }
  if (variable.source === 'journey') {
    return t('journeyDetails');
  }
  if (variable.source === 'action') {
    const action = variableGroups?.find((group) => group.id === variable.action);
    return action?.label ?? t('action');
  }
  return variable.source;
};

export const labelSeparator = ' • ';

export const getVariableLabel = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[]
) => {
  const prettySource = getVariableSource(variable, variableGroups);
  const variableLabel =
    variableGroups
      ?.map(
        (group) =>
          group.fields.find((field) => {
            return (
              variable.field === field.field &&
              (variable.action === 'undefined' || variable.action === undefined
                ? field.source === variable.source
                : variable.action === field.action)
            );
          })?.label
      )
      .find((label) => label !== undefined) ?? variable.label;

  const prefix = `${prettySource}${labelSeparator}`;
  if (variableLabel.startsWith(prefix)) {
    return variableLabel;
  }
  return `${prefix}${variableLabel}`;
};

export const normalizeVariables = (template?: string): string => {
  return template
    ? template
        .replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, key) => `{{ ${key.trim()} }}`)
        .replace(/(?<!\{)\{\s*([^}]+?)\s*\}(?!\})/g, (_, key) => `{ ${key.trim()} }`)
    : '';
};

export const transformInput = ({
  value,
  variables,
  variableGroups,
}: FieldMappingEditorValue & { variableGroups?: FieldMappingGroup[] }) => {
  let parsedValue = normalizeVariables(value);

  if (variables) {
    // Matches both double-brace {{ variable }} and single-brace { variable } syntax.
    // The alternation creates two capture groups: first captures from {{ }}, second from { }.
    // Only one group will have a value per match, so we use || to get whichever matched.
    const jinjaRegex = /\{\{\s*([^}]+?)\s*\}\}|\{\s*([^}]+?)\s*\}/g;

    parsedValue = parsedValue.replace(
      jinjaRegex,
      (match, doubleVariableName, singleVariableName) => {
        const variableName = doubleVariableName || singleVariableName;
        const variable = variables[variableName];
        if (!variable) return match;

        const { options, icon, iconRight, ...val } = variable;
        const variableLabel = getVariableLabel(val, variableGroups);

        return getPlaceholderHtml({
          ...val,
          variable: variableName,
          label: variableLabel,
          isActive: val.isActive ? val.isActive.toString() : '',
        });
      }
    );
  }

  return { value: parsedValue };
};

export const transformSimpleValueInput = (
  input: ExtendedFieldMappingEditorValue[],
  transformValue: (value: string) => string
) =>
  input.reduce(
    (accumulated: FieldMappingEditorValue, current) => {
      if (isFieldMappingEditorValue(current)) {
        accumulated.value = `${accumulated.value}${current.value}`;
        accumulated.variables = { ...accumulated.variables, ...current.variables };
      } else {
        accumulated.value = `${accumulated.value}${transformValue(current.value)}`;
      }
      return accumulated;
    },
    { value: '', variables: {} }
  );

export const transformSimpleValueOutput = (
  fieldMappingValue: FieldMappingEditorValue,
  placeholderTag: string
) => {
  const parser = new DOMParser();
  const html = parser.parseFromString(fieldMappingValue.value, 'text/html');
  const elements = [...html.getElementsByTagName(placeholderTag)];
  const output = fieldMappingValue.variables
    ? Object.entries(fieldMappingValue.variables).map((variable) => ({
        value: `{{ ${variable[0]} }}`,
        variables: { [variable[0]]: variable[1] },
      }))
    : ([] as ExtendedFieldMappingEditorValue[]);
  elements.forEach((element) => {
    const value = element.attributes.getNamedItem('value')?.nodeValue ?? '';
    output.push({ value });
  });
  return output;
};

export const getPlaceholderNodes = (state: EditorState | Transaction) => {
  const NATIVE_TYPES = ['paragraph', 'text'];
  const placeholderNodes: Node[] = [];
  state.doc.descendants((node) => {
    if (!NATIVE_TYPES.includes(node.type.name)) {
      placeholderNodes.push(node);
    }
  });
  return placeholderNodes;
};

export const extractName = (variable: MappableVariable) => {
  const variableName =
    variable.source === 'action'
      ? `${variable.action}_${variable.field}`
      : variable.source?.startsWith('journey_attribute__') ||
        variable.source?.startsWith('company_traveller_attribute__')
      ? `${variable.source}__${variable.field}`
      : `${variable.source}_${variable.field}`;
  return variableName;
};

export const getFieldContent = (label: string | { value: string }): string => {
  if (typeof label === 'string') return label;
  if (typeof label === 'object' && label !== null && 'value' in label) {
    return label.value;
  }
  return '';
};

/**
 * Maps variable groups into a flat record of variables by name
 */
export const mapVariableGroups = (variableGroups: FieldMappingGroup[] | undefined) => {
  const variables = {} as Record<string, MappableVariable>;
  if (!variableGroups) {
    return variables;
  }
  variableGroups.forEach((group) => {
    group.fields.forEach((field) => {
      if (field.isActive !== false) {
        if (field.type !== 'separator') {
          let variableName = '';
          if (field.field === 'unique_link') {
            variableName = 'action_unique_link';
          } else {
            variableName = extractName(field as MappableVariable);
          }
          variables[variableName] = field as MappableVariable;
        }
      }
    });
  });
  return variables;
};

export const templatePattern = /{{\s*(\w+)\s*}}|{\s*(\w+)\s*}/g;

/**
 * Removes unknown variables from text (variables not in the provided variables map)
 */
export const removeUnknownVariables = (text: string, variables: Record<string, any>) => {
  return text.replace(templatePattern, (match, partOne, partTwo) => {
    const variableName = partOne || partTwo;
    return variables.hasOwnProperty(variableName) ? match : '';
  });
};

/**
 * Finds variables in text that are not in the provided variables map
 */
export const findUnknownVariables = (text: string, variables: Record<string, any>): string[] => {
  const matches = [...text.matchAll(templatePattern)];

  const unknowns = new Set(
    matches
      .map((match) => match[1] || match[2])
      .filter((variableName) => !variables.hasOwnProperty(variableName))
  );

  return Array.from(unknowns);
};

type UseVariableStyledContentOptions = {
  /** The raw text content that may contain variable placeholders */
  content: string;
  variables: Record<string, MappableVariable>;
  variableGroups?: FieldMappingGroup[];
  icon?: typeof BaseIcon;
  hideRemove?: boolean;
  /** Whether to include full variable attributes in node (source, field, action, type) */
  includeFullAttrs?: boolean;
  suffix?: string;
  isEditMode?: boolean;
};

/**
 * Hook that transforms text with variable placeholders into styled content with blue tokens.
 * Use this when you need to display text that contains {{ variable }} placeholders.
 */
export const useVariableStyledContent = ({
  content,
  variables,
  variableGroups,
  icon: Icon = IconMappingOn,
  hideRemove = false,
  includeFullAttrs = true,
  suffix = '',
  isEditMode = false,
}: UseVariableStyledContentOptions) => {
  const cleanMessage = removeUnknownVariables(content, variables);
  const withSuffix = suffix ? `${cleanMessage}${suffix}` : cleanMessage;
  const text = withSuffix.replace(/\{\{\s*/g, '{').replace(/\s*\}\}/g, '}') || ' ';

  const { styledTranslate } = useScopedTranslate({ en: { text } });

  const contextValue = {
    showFieldMappingSidebar: () => {},
    variableGroups: variableGroups ?? [],
    isEditMode,
  };

  const variablesMap = Object.entries(variables).reduce((acc, [variableKey, variable]) => {
    const variableLabel = getVariableLabel(variable, variableGroups);
    const actionId = 'action' in variable ? variable.action : undefined;
    const isMissing = isSourceActionMissing({ source: variable.source, action: actionId ?? '' });
    const variant = isMissing ? 'error' : 'fieldMapped';
    const icon = isMissing ? IconMappingOff : Icon;
    acc[variableKey] = (
      <FieldMappingContext.Provider value={contextValue}>
        <PlaceholderContainer
          variant={variant}
          deleteNode={() => {}}
          icon={icon}
          hideRemove={hideRemove}
          // @ts-ignore-next-line
          node={{
            attrs: includeFullAttrs
              ? {
                  label: variableLabel,
                  source: variable.source,
                  field: variable.field,
                  action: actionId,
                  type: variable.type,
                  variable: variableKey,
                }
              : { label: variableLabel },
          }}
        />
      </FieldMappingContext.Provider>
    );
    return acc;
  }, {} as Record<string, JSX.Element>);

  return {
    cleanMessage,
    styledContent: styledTranslate('text', variablesMap),
  };
};

const locale = {
  en: {
    traveller: 'Traveller',
    company: 'Company',
    journeyDetails: 'Journey details',
    action: 'Action',
  },
};
